export const createChatId = (prefix = 'chat') => `${prefix}-${Math.random().toString(36).slice(2, 10)}`

export const wait = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })

const wcApiBaseURL = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_URL
const wcApiEmail = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_EMAIL
const wcApiPassword = import.meta.env.VITE_DATASET_WOOCOMMERCE_API_PASSWORD

const waitFor = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms)
  })

const parseLoginErrorPayload = async (response: Response): Promise<{ message?: string; error?: string }> => {
  try {
    return (await response.json()) as { message?: string; error?: string }
  } catch {
    const text = await response.text().catch(() => 'No response body')
    return { message: text }
  }
}

const runLoginAttempt = async (attempt: number, maxRetries: number): Promise<{ bearerToken: string; user: unknown }> => {
  const response = await fetch(`${wcApiBaseURL}/api/v1/auth/sign-in/email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: wcApiEmail, password: wcApiPassword }),
    cache: 'no-store',
    signal: AbortSignal.timeout(30_000)
  })

  if (!response.ok) {
    const errorData = await parseLoginErrorPayload(response)

    if (response.status >= 500 && attempt < maxRetries) {
      await waitFor(attempt * 3000)
      return runLoginAttempt(attempt + 1, maxRetries)
    }

    throw new Error(errorData.message || errorData.error || `External Service Error: ${response.status}`)
  }

  const bearerToken = response.headers.get('set-auth-token')

  if (!bearerToken) {
    throw new Error('Missing set-auth-token header')
  }

  const data = (await response.json()) as { user?: unknown }

  return { bearerToken, user: data.user }
}

const loginWithRetry = async (attempt: number, maxRetries: number): Promise<{ bearerToken: string; user: unknown }> => {
  try {
    return await runLoginAttempt(attempt, maxRetries)
  } catch (error) {
    const normalizedError =
      error instanceof Error && (error.name === 'TimeoutError' || error.name === 'AbortError')
        ? new Error(`Connection Timeout: External service (AO-API) took too long to respond (>30s).`)
        : error

    if (attempt >= maxRetries) {
      throw normalizedError
    }

    await waitFor(attempt * 3000)
    return loginWithRetry(attempt + 1, maxRetries)
  }
}

export async function login() {
  const maxRetries = 5

  if (!wcApiBaseURL || !wcApiEmail || !wcApiPassword) {
    throw new Error('Support chat environment variables are missing')
  }

  try {
    return await loginWithRetry(1, maxRetries)
  } catch (error) {
    throw error instanceof Error ? error : new Error('Support chat login failed')
  }
}
